import React, { useState, useEffect } from 'react';
import {
  string, shape as shapeType, func, number
} from 'prop-types';
import {
  arrayOf,
  params,
  shape,
  string as stringType,
  customType,
  useDataModel,
  extend,
} from '@thd-nucleus/data-sources';
import { Alert, Button } from '@thd-olt-component-react/core-ui';
import { useDrawer } from '@thd-olt-component-react/drawer';
import { Loading } from '../shared/Loading';
import { Footer } from '../shared/Footer';

import * as styles from '../shared/messages.module.scss';
import * as drawerStyles from '../shared/drawers.module.scss';
import { DRAWERS } from '../shared/Constants';

export const PriceChangeWarningDrawer = ({
  itemId,
  quantity,
  price,
  fulfillmentLocation,
  fulfillmentMethod,
  localStoreId,
  customerInfo,
  setQuoteName,
  setError,
  selectedQuote
}) => {
  const { closeDrawer, setCurrentDrawer } = useDrawer();
  const { svocId } = customerInfo;
  const [showLoader, setShowLoader] = useState(false);

  const [
    addToThisQuote,
    { data: addToQuoteData, loading: addToQuoteLoading, error: addToQuoteError }
  ] = useDataModel('addItemToQuote', {
    variables: {
      quoteId: selectedQuote?.quoteId,
      svocId,
      userId: selectedQuote?.userId,
      lineItems: [{
        itemId,
        quantity,
        fulfillmentLocation,
        fulfillmentMethod
      }],
      localStoreId
    },
    ssr: false
  });

  const addItemToQuote = () => {
    setShowLoader(true);
    addToThisQuote();
  };

  const triggerAnalytics = () => {
    const quoteId = addToQuoteData?.addItemToQuote?.quoteId;

    window.LIFE_CYCLE_EVENT_BUS.trigger('add-to-quote.quoteAdd', {
      quoteType: 'online',
      quoteId,
      item: {
        quoteLocation: 'pip',
        quantity,
        price: {
          basePrice: price
        },
        productInfo: {
          sku: itemId
        }
      }
    });
  };

  useEffect(() => {
    if (addToQuoteData && !addToQuoteLoading && !addToQuoteError) {
      setShowLoader(false);
      setQuoteName(selectedQuote.quoteName);
      setCurrentDrawer(DRAWERS.SUCCESS);
      triggerAnalytics();
    } else if (addToQuoteError) {
      setShowLoader(false);
      setError(addToQuoteError);
      setCurrentDrawer(DRAWERS.ERROR);
    }
  }, [addToQuoteData, addToQuoteLoading, addToQuoteError]);

  return (
    <div className={drawerStyles.container}>
      <div className={drawerStyles.content}>
        {showLoader && <Loading />}
        <div className={styles.content}>
          <Alert title="Update Quoted Pricing" type="warning" inline />
          <p className={styles.messageText}>Prices on your existing quote
            <span className={styles.itemName}>{` ${selectedQuote.quoteName} `}</span>
            will update when you add this item. Are you sure you want to proceed?
          </p>
        </div>
      </div>
      <Footer>
        <Button className={styles.button} onClick={addItemToQuote}>Yes, Add Item to Quote</Button>
        <Button outline className={styles.button} onClick={closeDrawer}>Cancel</Button>
      </Footer>
    </div>
  );
};

PriceChangeWarningDrawer.propTypes = {
  customerInfo: shapeType({
    svocId: string.isRequired,
    userId: string.isRequired,
  }).isRequired,
  itemId: string.isRequired,
  quantity: string.isRequired,
  price: number.isRequired,
  fulfillmentLocation: string.isRequired,
  fulfillmentMethod: string.isRequired,
  localStoreId: string.isRequired,
  setQuoteName: func.isRequired,
  setError: func.isRequired,
  selectedQuote: shapeType({
    quoteId: string.isRequired,
    quoteName: string.isRequired,
    userId: string.isRequired
  }).isRequired
};

PriceChangeWarningDrawer.dataModel = extend(
  {
    addItemToQuote: params({
      quoteId: stringType().isRequired(),
      svocId: stringType().isRequired(),
      userId: stringType().isRequired(),
      lineItems: arrayOf(
        customType('LineItemInput!').shape({
          itemId: stringType(),
          quantity: stringType(),
          fulfillmentLocation: stringType(),
          fulfillmentMethod: stringType()
        })
      ).isRequired(),
      localStoreId: stringType().isRequired(),
    }).mutation().shape({
      quoteId: stringType(),
      errorData: arrayOf(
        shape({
          errors: arrayOf(
            shape({
              id: stringType(),
              description: stringType(),
              submessage: stringType(),
              errorCode: stringType(),
              errorProperties: shape({
                errorProperty: shape({
                  name: stringType(),
                  value: stringType()
                })
              })
            })
          ),
          httpStatus: stringType(),
          userId: stringType(),
          customerAccountId: stringType(),
          correlationId: stringType(),
          correlationType: stringType()
        })
      )
    })
  });